import React, { useEffect, useRef, useState } from 'react'
import { translationMemory, TMMatch } from '../../lib/cat/translationMemory'
import type { SuggestionSource } from '../../lib/cat/segmentAnalytics'
import { ChevronDown, ChevronUp, BookOpen, CornerDownLeft } from 'lucide-react'

interface Props {
  sourceText: string
  sourceLang: string
  targetLang: string
  onInsert: (text: string, source?: SuggestionSource) => void
}

export const TMHorizontalPanel: React.FC<Props> = ({
  sourceText, sourceLang, targetLang, onInsert,
}) => {
  const [matches, setMatches] = useState<TMMatch[]>([])
  const [loading, setLoading] = useState(false)
  const [collapsed, setCollapsed] = useState(false)
  const [minScore] = useState(70)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!sourceText?.trim()) { setMatches([]); return }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      const results = await translationMemory.search(sourceText, sourceLang, targetLang, minScore)
      setMatches(results)
      setLoading(false)
    }, 350)
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current) }
  }, [sourceText, sourceLang, targetLang, minScore])

  const scoreBadge = (score: number) => {
    if (score === 100) return 'bg-emerald-100 text-emerald-700 border border-emerald-300'
    if (score >= 85)  return 'bg-amber-100 text-amber-700 border border-amber-300'
    return 'bg-blue-100 text-blue-700 border border-blue-300'
  }

  const rowAccent = (score: number) => {
    if (score === 100) return 'border-l-2 border-l-emerald-400'
    if (score >= 85)  return 'border-l-2 border-l-amber-400'
    return 'border-l-2 border-l-blue-400'
  }

  return (
    <div className="bg-white border-t-2 border-gold-500 select-none">

      {/* ── Header ── */}
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center gap-2 px-4 py-1.5 bg-slate-900 hover:bg-slate-800 transition-colors"
      >
        <BookOpen className="w-3.5 h-3.5 text-gold-600 shrink-0" />
        <span className="text-xs font-bold text-slate-200 uppercase tracking-wider">
          Translation Memory
        </span>
        {loading ? (
          <span className="ml-1 text-xs text-gray-200 italic">searching…</span>
        ) : matches.length > 0 ? (
          <span className="ml-1 px-2 py-0.5 rounded-full bg-gold-100 text-gold-700 text-xs font-bold border border-gold-300">
            {matches.length} match{matches.length !== 1 ? 'es' : ''}
          </span>
        ) : sourceText?.trim() ? (
          <span className="ml-1 text-xs font-medium text-slate-100">no matches above {minScore}%</span>
        ) : null}
        <span className="ml-auto text-slate-200">
          {collapsed
            ? <ChevronDown className="w-3.5 h-3.5" />
            : <ChevronUp className="w-3.5 h-3.5" />}
        </span>
      </button>

      {/* ── Match rows ── */}
      {!collapsed && (
        <div className="space-y-1 px-4 pb-3">
          {loading ? (
            <div className="flex items-center gap-2 py-2">
              <div className="w-4 h-4 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-gray-200">Searching translation memory…</span>
            </div>
          ) : matches.length === 0 ? (
            <p className="text-xs text-gray-200 py-1 italic">
              {sourceText?.trim()
                ? `No TM matches above ${minScore}% — confirm segments to build your TM`
                : 'Select a segment to search translation memory'}
            </p>
          ) : matches.map((match, idx) => (
            <div
              key={idx}
              className={`flex items-stretch rounded-lg border border-gray-200 bg-white overflow-hidden ${rowAccent(match.score)}`}
            >
              {/* LEFT col — mirrors segment number column (w-16) */}
              <div className="flex-shrink-0 w-16 flex flex-col items-center justify-center gap-1 py-2 px-1 border-r border-gray-200 bg-gray-50">
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded-full ${scoreBadge(match.score)}`}>
                  {match.score}%
                </span>
                <span className="text-xs text-gray-700 text-center leading-tight">
                  {match.matchType === 'exact' ? '⚡ exact' : '≈ fuzzy'}
                </span>
                <span className="text-xs text-gray-700">{match.entry.usage_count}×</span>
              </div>

              {/* CENTER — mirrors segment grid-cols-2 */}
              <div className="flex-1 grid grid-cols-2 gap-0 min-w-0">
                {/* Source */}
                <div className="px-3 py-2 border-r border-gray-200 min-w-0">
                  <div className="text-xs font-medium text-gray-700 mb-1">Source</div>
                  <p className="text-xs text-gray-700 leading-relaxed">
                    {match.entry.source_text}
                  </p>
                </div>
                {/* Target */}
                <div className="px-3 py-2 min-w-0">
                  <div className="text-xs font-medium text-gray-700 mb-1">Translation</div>
                  <p className="text-xs text-gray-900 font-medium leading-relaxed">
                    {match.entry.target_text}
                  </p>
                </div>
              </div>

              {/* RIGHT col — mirrors segment action column (w-10) */}
              <button
                onClick={() => onInsert(match.entry.target_text, { kind: 'tm', score: match.score })}
                className="flex-shrink-0 w-10 flex flex-col items-center justify-center gap-1 border-l border-gray-200 bg-gray-50 hover:bg-gold-50 hover:text-gold-700 text-gray-700 transition-all group"
                title="Insert this translation (click)"
              >
                <CornerDownLeft className="w-4 h-4 group-hover:text-gold-600" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
